// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useState, useContext, useEffect, useMemo } from "react";
import "./SuppliersByMaterial.scss";
import SupplierTable from "./SupplierTable";
import { AuthContext } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";
import BrushIcon from "@mui/icons-material/Brush";

import {
  filterSupplierData,
  filterSuppliersBySearchQuery,
} from "./SupplierDataService";

import {
  getTableEntriesFromDeliveriesData,
  getTableEntriesFromObservationsData,
} from "../../components/Suppliers/SupplierTableDataLoader";

function SuppliersByMaterial({
  material,
  suppliers,
  supplierData,
  supplierDataFiltered,
  tableEntries,
  activeFilters,
  searchQuery,
  isLoading = false,
  sortedColumns,
}) {
  const [expanded, setExpanded] = useState(true);
  const { labelsets } = useContext(AuthContext);
  const { t } = useTranslation();

  // Function to get material display name
  function getMaterialDisplayName(materialKey) {
    return (
      labelsets?.materials?.labels?.[materialKey]?.display_name ||
      materialKey ||
      "Unknown"
    );
  }

  function formatTons(tons) {
    return `${tons.toLocaleString("de-CH", {
      maximumFractionDigits: 2,
    })} t`.replace(",", "'");
  }

  function formatPercentage(value) {
    return value !== null && value !== undefined
      ? `${(value * 100).toFixed(1)}%`
      : "-";
  }

  function toggleExpand() {
    setExpanded(!expanded);
  }

  const filterDependencies = {
    materialFilter: material,
    minDeliveriesFilter: activeFilters.deliveryRange.min,
    maxDeliveriesFilter: activeFilters.deliveryRange.max,
    minTonnageFilter: activeFilters.tonnageRange.min,
    maxTonnageFilter: activeFilters.tonnageRange.max,
    minBalesInProductionFilter: activeFilters.balesProductionRange.min,
    maxBalesInProductionFilter: activeFilters.balesProductionRange.max,
    selectedClaimIssuesFilter: null,
  };
  const supplierDataGrouped = useMemo(() => {
    return filterSupplierData({
      suppliersData: supplierDataFiltered,
      ...filterDependencies,
    });
  }, [supplierDataFiltered, ...Object.values(filterDependencies)]);

  // Get table entries for filtered supplier data
  const tableEntriesGrouped = useMemo(() => {
    return {
      ...getTableEntriesFromDeliveriesData(supplierDataGrouped),
      ...getTableEntriesFromObservationsData(supplierDataGrouped),
    };
  }, [supplierDataGrouped]);

  const filteredSuppliers = useMemo(() => {
    const suppliersWithData = suppliers.filter(
      (supplier) => supplier.id in supplierDataGrouped
    );
    return filterSuppliersBySearchQuery(suppliersWithData, searchQuery);
  }, [suppliers, suppliers, supplierDataGrouped, searchQuery]);

  // Get the overview metrics for this material
  const materialDisplayName = getMaterialDisplayName(material);
  const totalDeliveries = tableEntriesGrouped?.deliveriesCount?.total || 0;
  const totalIssues = tableEntriesGrouped?.claimsCount?.total || 0;
  const totalTons = tableEntriesGrouped?.totalWeightTons?.total || 0;
  const brownPaperAverage =
    tableEntriesGrouped?.brownPaperContent?.average || 0;

  return (
    <div className="suppliers-by-material">
      <div
        className={`material-header ${expanded ? "expanded" : ""}`}
        onClick={toggleExpand}
      >
        <div className="material-info">
          <div className="material-icon">
            {/* Package icon from Lucide */}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
              <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              <polyline points="3.29 7 12 12 20.71 7"></polyline>
              <line x1="12" y1="22" x2="12" y2="12"></line>
            </svg>
          </div>
          <div>
            <h3 className="material-name">{materialDisplayName}</h3>
            <div className="material-stats">
              <span className="supplier-count">
                {filteredSuppliers.length}{" "}
                {filteredSuppliers.length === 1
                  ? t("supplier_count_singular")
                  : t("supplier_count_plural")}
              </span>
              <span className="delivery-count">
                {totalDeliveries} {t("deliveries_count_material")}
              </span>
              <span className="total-tons">{formatTons(totalTons)}</span>
              {totalIssues > 0 && (
                <span
                  className={`issue-count ${
                    totalIssues > 5 ? "status-error" : "status-neutral"
                  }`}
                >
                  {totalIssues}{" "}
                  {totalIssues === 1
                    ? t("issue_count_singular")
                    : t("issue_count_plural")}
                </span>
              )}
              <span className="brown-paper-stat">
                <BrushIcon />
                {t("avg_brown_paper_percentage")}:{" "}
                {formatPercentage(brownPaperAverage)}
              </span>
            </div>
          </div>
        </div>

        <div className="material-metrics">
          <button
            className="expand-button"
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand();
            }}
          >
            {expanded ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            )}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="material-content">
          <SupplierTable
            suppliers={filteredSuppliers}
            supplierData={supplierData}
            supplierDataFiltered={supplierDataFiltered}
            supplierDataGrouped={supplierDataGrouped}
            tableEntries={tableEntries}
            tableEntriesGrouped={tableEntriesGrouped}
            activeFilters={{ ...activeFilters, material: material }}
            defaultSortColumn={"name"}
            defaultSortDirection={"asc"}
            defaultPageSize={5}
            showSearchInfo={false}
            isLoading={isLoading}
            sortedColumns={sortedColumns}
          />
        </div>
      )}
    </div>
  );
}

export default SuppliersByMaterial;
